/* eslint-disable @typescript-eslint/no-explicit-any */
import { getIntlLocale, translate, translatePlural } from "@/i18n/translate";
import type { TranslationKey } from "@/i18n/messages";

/**
 * Automatic findings of the developer view: places where a stored evaluation contradicts itself or shows
 * that a part of the pipeline did not do its job, so that a wrong verdict can be traced to its step.
 * The rules mirror pizza-backend (pipeline.overall_evaluation, the photo-quality checks, the CNNs).
 * Titles and details are texts in the current UI language (built when diagnosePizza runs).
 */

export type FindingSeverity = "error" | "warn" | "info";

/** anchor of the diagnostics section the finding belongs to */
export type DiagnosticsSection =
  | "timing"
  | "record"
  | "detection"
  | "photo"
  | "components"
  | "sauce"
  | "doneness"
  | "saver"
  | "slicing"
  | "layers";

export interface Finding {
  severity: FindingSeverity;
  section: DiagnosticsSection;
  title: string;
  detail?: string;
}

const pct = (value: number, digits = 1) =>
  new Intl.NumberFormat(getIntlLocale(), {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
/** seconds with two decimals in the UI language */
const secondsText = (value: number) =>
  new Intl.NumberFormat(getIntlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** |p - threshold| below this is reported as a borderline decision */
const BORDERLINE_MARGIN = 0.05;
/** whole pipeline / single step durations (seconds) worth a look */
const SLOW_TOTAL_S = 5;
const SLOW_STEP_S = 2;
/** an evaluation this long after the capture was a backfill or a re-evaluation */
const LATE_EVALUATION_S = 120;

/** layers a full evaluation (a pizza was found) always stores */
const EXPECTED_LAYERS = ["pizza_mask", "crust", "heatmap"];

export function diagnosePizza({
  pizza,
  metadata,
  layers,
  evaluatedAt,
}: {
  pizza?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  layers?: { name: string }[] | null;
  evaluatedAt?: string | null;
}): Finding[] {
  const findings: Finding[] = [];
  const add = (finding: Finding) => findings.push(finding);

  if (!metadata) {
    add({
      severity: "error",
      section: "record",
      title: translate("diag.finding.noMetadata"),
      detail: translate("diag.finding.noMetadataDetail"),
    });
    return findings;
  }

  const classification = String(metadata.classification ?? "").toLowerCase();
  const shortCircuited = classification === "no pizza" || classification === "invalid photo";

  // ---- overall verdict vs. its parts (pipeline.overall_evaluation) ----
  if (!shortCircuited && metadata.overall_evaluation?.status) {
    const ce = metadata.components_evaluation ?? {};
    const parts: [TranslationKey, boolean | undefined][] = [
      ["diag.part.ingredients", ce.base_components ? ce.base_components.status === "OK" : undefined],
      ["diag.part.removals", ce.removals ? ce.removals.status === "OK" : undefined],
      ["diag.part.extras", ce.extras ? ce.extras.status === "OK" : undefined],
      ["diag.part.burnt", metadata.doneness_evaluation?.burnt ? metadata.doneness_evaluation.burnt.status === false : undefined],
      ["diag.part.raw", metadata.doneness_evaluation?.raw ? metadata.doneness_evaluation.raw.status === false : undefined],
      ["diag.part.saver", metadata.pizza_saver_evaluation ? metadata.pizza_saver_evaluation.status === "OK" : undefined],
      ["diag.part.photo", metadata.photo_quality_evaluation ? metadata.photo_quality_evaluation.status === "OK" : undefined],
    ];
    const missing = parts.filter(([, ok]) => ok === undefined).map(([name]) => translate(name));
    const recomputed = parts.every(([, ok]) => ok === true) ? "OK" : "NOK";
    if (recomputed !== metadata.overall_evaluation.status) {
      add({
        severity: "error",
        section: "record",
        title: translate("diag.finding.overallMismatch", {
          status: metadata.overall_evaluation.status,
          recomputed,
        }),
        detail: missing.length
          ? translate("diag.finding.overallMissingParts", { parts: missing.join(", ") })
          : translate("diag.finding.overallDisagrees"),
      });
    } else if (missing.length) {
      add({
        severity: "warn",
        section: "record",
        title: translate("diag.finding.lacksParts", { parts: missing.join(", ") }),
        detail: translate("diag.finding.lacksPartsDetail"),
      });
    }
  }

  // ---- detection / geometry ----
  if (!shortCircuited && !metadata.pizza_roi) {
    add({ severity: "warn", section: "detection", title: translate("diag.finding.noRoi") });
  }
  const presence = metadata.pizza_detection_evaluation?.details;
  if (presence?.tier && presence.tier !== "ring") {
    add({
      severity: "info",
      section: "detection",
      title: translate("diag.finding.presenceTier", { tier: presence.tier }),
      detail: translate("diag.finding.presenceTierDetail"),
    });
  }

  // ---- photo quality ----
  const checks = metadata.photo_quality_evaluation?.checks ?? {};
  const blur = checks.pizza_blur?.details;
  if (blur?.skipped) {
    add({ severity: "warn", section: "photo", title: translate("diag.finding.blurSkipped"), detail: String(blur.skipped) });
  } else if (blur) {
    if (blur.enforced === false && blur.would_fail) {
      add({
        severity: "warn",
        section: "photo",
        title: translate("diag.finding.blurShadow"),
        detail: translate("diag.finding.blurShadowDetail", {
          p: pct(blur.p_blurred),
          threshold: pct(blur.threshold, 0),
        }),
      });
    }
    if (isNumber(blur.p_blurred) && isNumber(blur.threshold) && Math.abs(blur.p_blurred - blur.threshold) < BORDERLINE_MARGIN) {
      add({
        severity: "info",
        section: "photo",
        title: translate("diag.finding.blurBorderline"),
        detail: translate("diag.finding.blurBorderlineDetail", {
          p: pct(blur.p_blurred),
          threshold: pct(blur.threshold, 0),
        }),
      });
    }
  }
  const obstacle = checks.obstacle_in_pizza?.details;
  const segmentation = obstacle?.segmentation;
  if (checks.obstacle_in_pizza && !segmentation) {
    add({
      severity: "info",
      section: "photo",
      title: translate("diag.finding.obstacleNoSegmentation"),
      detail: translate("diag.finding.obstacleNoSegmentationDetail"),
    });
  } else if (segmentation?.skipped) {
    add({
      severity: "warn",
      section: "photo",
      title: translate("diag.finding.obstacleSkipped"),
      detail: String(segmentation.skipped),
    });
  } else if (segmentation && segmentation.enforced === false && segmentation.would_fail) {
    add({
      severity: "warn",
      section: "photo",
      title: translate("diag.finding.obstacleShadow"),
      detail: translate("diag.finding.obstacleShadowDetail", {
        coverage: pct(segmentation.coverage),
        threshold: pct(segmentation.threshold, 0),
      }),
    });
  }
  const visibility = checks.pizza_visibility?.details;
  if (visibility && isNumber(visibility.frame_overshoot_ratio) && visibility.frame_overshoot_ratio > 0) {
    add({
      severity: checks.pizza_visibility.status === "NOK" ? "warn" : "info",
      section: "photo",
      title: translate("diag.finding.frameEdge", { value: pct(visibility.frame_overshoot_ratio) }),
      detail: isNumber(visibility.max_overshoot_ratio)
        ? translate("diag.finding.frameEdgeDetail", { value: pct(visibility.max_overshoot_ratio) })
        : undefined,
    });
  }

  // ---- doneness model ----
  const doneness = metadata.doneness_evaluation;
  const model = metadata.burnt_model;
  const verdictCheck = (
    texts: { mismatch: TranslationKey; borderline: TranslationKey },
    status: boolean | undefined,
    probability: unknown,
    threshold: unknown,
  ) => {
    if (!isNumber(probability) || !isNumber(threshold) || status === undefined) return;
    if ((probability >= threshold) !== Boolean(status)) {
      add({
        severity: "error",
        section: "doneness",
        title: translate(texts.mismatch),
        detail: translate("diag.finding.mismatchDetail", {
          p: pct(probability),
          threshold: pct(threshold, 0),
          status: translate(status ? "status.nok" : "status.ok"),
        }),
      });
    } else if (Math.abs(probability - threshold) < BORDERLINE_MARGIN) {
      add({
        severity: "info",
        section: "doneness",
        title: translate(texts.borderline),
        detail: translate("diag.finding.borderlineDetail", { p: pct(probability), threshold: pct(threshold, 0) }),
      });
    }
  };
  if (doneness?.burnt) {
    verdictCheck(
      { mismatch: "diag.finding.burntMismatch", borderline: "diag.finding.burntBorderline" },
      doneness.burnt.status,
      doneness.burnt.probability ?? model?.probability,
      model?.threshold,
    );
  }
  if (doneness?.raw && doneness.raw.evaluated !== false) {
    verdictCheck(
      { mismatch: "diag.finding.rawMismatch", borderline: "diag.finding.rawBorderline" },
      doneness.raw.status,
      doneness.raw.probability ?? model?.raw_probability,
      model?.raw_threshold,
    );
  }
  if (!shortCircuited && doneness && !model && !metadata.quarter_results) {
    add({ severity: "info", section: "doneness", title: translate("diag.finding.noProvenance") });
  }
  if (isNumber(model?.crust_fraction) && model.crust_fraction < 0.25) {
    add({
      severity: "warn",
      section: "doneness",
      title: translate("diag.finding.lowCrust", { value: pct(model.crust_fraction, 0) }),
      detail: translate("diag.finding.lowCrustDetail"),
    });
  }

  // ---- components ----
  const base = metadata.components_evaluation?.base_components;
  if (base) {
    const expected = (base.expected?.length ?? 0) + (base.non_detectable?.length ?? 0);
    if (expected > 0 && (base.non_detectable?.length ?? 0) === expected) {
      add({
        severity: "info",
        section: "components",
        title: translate("diag.finding.noDetectable"),
        detail: translate("diag.finding.noDetectableDetail"),
      });
    }
  }
  const lowConfidence = (metadata.components_bbs ?? []).filter(
    (bb: any) => isNumber(bb.confidence) && bb.confidence < 0.5,
  );
  if (lowConfidence.length) {
    add({
      severity: "info",
      section: "components",
      title: translatePlural("diag.finding.lowConfidence", lowConfidence.length),
      detail: lowConfidence
        .map((bb: any) => `${bb.class ?? bb.label ?? bb.model_class} ${pct(bb.confidence, 0)}`)
        .join(", "),
    });
  }

  // ---- slicing ----
  const slicing = metadata.slicing_model;
  if (slicing?.skipped) {
    add({
      severity: "info",
      section: "slicing",
      title: translate("diag.finding.slicingSkipped"),
      detail: String(slicing.skipped),
    });
  }

  // ---- layers ----
  if (!shortCircuited && layers) {
    const names = new Set(layers.map((layer) => layer.name));
    const missingLayers = EXPECTED_LAYERS.filter((name) => !names.has(name));
    if (missingLayers.length) {
      add({
        severity: "warn",
        section: "layers",
        title: translatePlural("diag.finding.missingLayers", missingLayers.length, {
          names: missingLayers.join(", "),
        }),
      });
    }
  }

  // ---- timing ----
  const times = metadata.processing_times as Record<string, number> | undefined;
  if (times) {
    if (isNumber(times.total) && times.total > SLOW_TOTAL_S) {
      add({
        severity: "warn",
        section: "timing",
        title: translate("diag.finding.slowTotal", { value: secondsText(times.total) }),
      });
    }
    for (const [step, seconds] of Object.entries(times)) {
      if (step !== "total" && isNumber(seconds) && seconds > SLOW_STEP_S) {
        add({
          severity: "info",
          section: "timing",
          title: translate("diag.finding.slowStep", { step, value: secondsText(seconds) }),
        });
      }
    }
  }
  if (pizza?.capture_at && evaluatedAt) {
    const delay = (new Date(evaluatedAt).getTime() - new Date(pizza.capture_at).getTime()) / 1000;
    if (Number.isFinite(delay) && delay > LATE_EVALUATION_S) {
      add({
        severity: "info",
        section: "timing",
        title: translate("diag.finding.lateEvaluation", { delay: formatDelay(delay) }),
        detail: translate("diag.finding.lateEvaluationDetail"),
      });
    }
  }
  if ((pizza?.capture_count ?? 0) > 1) {
    add({
      severity: "info",
      section: "record",
      title: translate("diag.finding.recaptured", { count: pizza!.capture_count }),
      detail: translate("diag.finding.recapturedDetail"),
    });
  }

  const order: Record<FindingSeverity, number> = { error: 0, warn: 1, info: 2 };
  return findings.sort((a, b) => order[a.severity] - order[b.severity]);
}

/** a delay as text in the UI language: "45 s", "12 min", "1.5 h", "3 days" */
export function formatDelay(seconds: number) {
  if (seconds < 120) return `${Math.round(seconds)} s`;
  if (seconds < 7200) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) {
    const hours = new Intl.NumberFormat(getIntlLocale(), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(seconds / 3600);
    return `${hours} h`;
  }
  return translatePlural("diag.delay.days", Math.round(seconds / 86400));
}
